import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, Trash2, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";

const TYPES = ["سيارة", "دراجة نارية", "فان", "شاحنة", "باص"];

export const RentalFleetImport = ({ prices, onDone }) => {
  const inp = useRef(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const pick = async (files) => {
    const list = [...files];
    for (let i = 0; i < list.length; i += 1) {
      setBusy(`جارٍ قراءة ${i + 1} من ${list.length}…`);
      try {
        const r = await api.rentalFleetParse(list[i]);
        setRows((prev) => {
          const seen = new Set(prev.map((x) => x.identifier.replace(/\s+/g, "")));
          return [...prev, ...r.vehicles.filter((v) => !seen.has(v.identifier.replace(/\s+/g, ""))).map((v) => ({ ...v, on: !v.exists }))];
        });
      } catch (e) { toast.error(`${list[i].name}: ${e?.response?.data?.detail || "تعذّرت القراءة"}`); }
    }
    setBusy("");
    if (inp.current) inp.current.value = "";
  };
  const upd = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const chosen = rows.filter((r) => r.on && r.identifier.trim());
  const add = async () => {
    setBusy("جارٍ الإضافة…");
    try {
      const body = Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, Number(v) || 0]));
      const r = await api.rentalFleetBulkAdd(chosen, body);
      toast.success(`تمت إضافة ${r.added} مركبة${r.skipped ? ` · تخطّي ${r.skipped} (مكررة)` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); } finally { setBusy(""); }
  };
  return (
    <div className="min-w-0 space-y-2 overflow-hidden rounded-xl border border-dashed border-blue-300 bg-blue-50/50 p-3" data-testid="rental-fleet-import">
      <input ref={inp} type="file" multiple accept=".xlsx,.xlsm,.pdf,image/*" className="hidden" onChange={(e) => e.target.files?.length && pick(e.target.files)} data-testid="rental-fleet-import-input" />
      <button type="button" onClick={() => inp.current?.click()} disabled={!!busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 text-sm font-bold text-blue-700 shadow-sm hover:bg-blue-50" data-testid="rental-fleet-import-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {busy || "استيراد تلقائي: ملف «تم» (Excel) أو صور / PDF الاستمارات"}</button>
      <p className="flex items-center gap-1 text-[11px] text-slate-500"><Sparkles className="h-3 w-3 text-violet-500" /> تقدر تختار كذا ملف مرة وحدة. الاستمارات تُقرأ بالذكاء الاصطناعي، وراجع البيانات قبل الإضافة. الأسعار تحت تنطبق على الكل.</p>
      {rows.length > 0 && (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white" data-testid="rental-fleet-import-rows">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-center gap-2 border-b border-slate-100 p-2 text-xs last:border-0 ${r.on ? "" : "opacity-50"}`} data-testid={`rental-fleet-import-row-${i}`}>
              <input type="checkbox" checked={r.on} onChange={(e) => upd(i, "on", e.target.checked)} className="h-4 w-4 accent-blue-600" />
              <input value={r.identifier} onChange={(e) => upd(i, "identifier", e.target.value)} className="w-28 rounded border border-slate-200 px-1.5 py-1 font-bold" />
              <select value={r.vtype || "سيارة"} onChange={(e) => upd(i, "vtype", e.target.value)} className="rounded border border-slate-200 px-1 py-1">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <span className="min-w-0 flex-1 truncate text-slate-500">{[r.make, r.model, r.year, r.color].filter(Boolean).join(" · ")}{r.registration_expiry ? ` · تنتهي ${r.registration_expiry}` : ""}</span>
              {r.exists && <span className="shrink-0 rounded bg-amber-50 px-1.5 text-[10px] font-bold text-amber-700">مضافة سابقاً</span>}
              <button type="button" onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="shrink-0 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>))}
        </div>
      )}
      {rows.length > 0 && <Button type="button" onClick={add} disabled={!!busy || !chosen.length} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="rental-fleet-import-add">إضافة المحدد ({chosen.length}) بالأسعار المكتوبة</Button>}
    </div>
  );
};
